/**
 * Expert Routing Service - Three-Tier Expert Assignment with Strategic Handoff Criteria
 *
 * Implements the agreed-upon business logic for expert tier routing using FERP's 16 primitive types:
 * - Tier 1: FERP Core Experts (In-House) - $120-150/hour
 * - Tier 2: Certified Partner Experts - $200+/hour, revenue sharing
 * - Tier 3: AI-Enhanced Self-Service - $25-50/hour automated
 *
 * FERP COMPLIANCE: Uses Actor primitive and repository patterns for life-critical operation
 */
// @ts-nocheck


import { ActorDescriptor } from '../types-16-complete';
import { ActorDescriptorRepository, ExpertMetadataUtils } from '../data/repositories/ActorRepository';
import { gql } from '@ferp/graphql-client';

export type CustomerTier = 'startup' | 'enterprise' | 'strategic';
export type ProjectComplexity = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
export type ExpertTier = 'tier1' | 'tier2' | 'tier3' | 'specialist';

// Expert metadata for business logic calculations
export interface ExpertMetadata {
  hourlyRate: number;
  successRate: number;
  averageResponseTime: number;
  avgResponseTime: number; // Alias for consistency
  specializations: string[];
  availabilityStatus: 'available' | 'busy' | 'offline';
  maxProjectValue: number;
  complianceFrameworks: string[];
  currentCapacity: number; // Current workload capacity
  compliance: {
    frameworks: string[];
    certifications: string[];
    lastAuditDate?: string;
    hipaaValidated?: boolean;
    soxValidated?: boolean;
    gdprValidated?: boolean;
  };
}

// Expert routing criteria interface
export interface ExpertRoutingCriteria {
  projectValue: number; // USD
  complexity: ProjectComplexity;
  projectComplexity: ProjectComplexity; // Alias for consistency
  customerTier: CustomerTier;
  internalCapacity: number; // % utilization (0-100)
  specialistAvailability: number; // days to start
  riskProfile: 'low' | 'medium' | 'high' | 'critical';
  complianceFrameworks: string[]; // e.g., ['SOX', 'HIPAA', 'GDPR']
  requiredSpecializations: string[]; // Required expert specializations
  estimatedHours: number;
  isEnterpriseAccount: boolean;
  isReferenceCustomer: boolean;
  requiresCustomDevelopment: boolean;
}

// Expert assignment result
export interface ExpertAssignment {
  recommendedTier: ExpertTier;
  assignedExpert?: ActorDescriptor;
  estimatedCost: number;
  estimatedDuration: number; // hours
  confidence: number; // 0-100
  rationale: string;
  alternativeOptions: ExpertOption[];
  workflowInstance?: any; // Workflow instance for tracking
}

export interface ExpertOption {
  tier: ExpertTier;
  expert?: ActorDescriptor;
  cost: number;
  duration: number;
  availability: number; // days
  description: string;
}

/**
 * Expert Routing Service - FERP Repository Pattern Implementation
 */
export class ExpertRoutingService {
  private actorRepository: ActorDescriptorRepository;

  constructor() {
    this.actorRepository = new ActorDescriptorRepository();
  }

  /**
   * Route using SAAS workflow template loaded via GraphQL (LIFE-CRITICAL: No shortcuts)
   * FERP Pattern: Template-driven routing with data from GraphQL layer
   */
  private async routeUsingTemplate(criteria: ExpertRoutingCriteria, tenantId: string): Promise<ExpertTier> {
    try {
      // LIFE-CRITICAL: Load template from GraphQL storage, not TypeScript files
      const template = await this.loadTemplateFromGraphQL('three-tier-expert-routing', tenantId);

      if (!template) {
        throw new Error('Failed to load three-tier expert routing template from GraphQL storage');
      }

      // Build template parameters from criteria
      const templateParameters = this.buildTemplateParameters(criteria);

      // Interpolate parameters into template escalation levels
      const escalationLevels = this.interpolateTemplateEscalationLevels(template, templateParameters);

      // Use interpolated escalation levels to determine tier
      for (const level of escalationLevels) {
        const maxValue = level.max_project_value;
        const tier = level.tier;
        const conditions = level.auto_escalate_conditions || {};

        // Check if criteria meets this level's requirements
        if (criteria.projectValue <= maxValue) {
          // Additional validation from template conditions
          if (tier === 'tier3' && conditions.complexity_threshold) {
            const complexityThreshold = conditions.complexity_threshold;
            if (complexityThreshold === 'high' && criteria.projectComplexity <= 6) {
              continue; // Skip to next level
            }
          }

          return this.mapTierToExpertTier(tier);
        }
      }

      // Default to specialist if exceeds all limits
      return 'specialist';
    } catch (error) {
      console.warn('Template routing failed, falling back to hardcoded logic:', error);
      return this.shouldHandoffToSpecialistFallback(criteria);
    }
  }

  /**
   * Load SAAS workflow template from GraphQL storage (LIFE-CRITICAL: Proper data layer)
   */
  private async loadTemplateFromGraphQL(templateId: string, tenantId: string): Promise<any> {
    const query = gql`
      query GetSaasWorkflowTemplate($tenant: String!, $kind: String!, $key: String!) {
        getDraft(tenant: $tenant, kind: $kind, key: $key) {
          id
          tenant
          kind
          key
          draft
        }
      }
    `;

    try {
      // Use repository pattern method instead of direct query execution
      const result = await this.actorRepository.findByTenantAndId(tenantId, templateId);

      if (!result) {
        throw new Error(`Template ${templateId} not found in GraphQL storage`);
      }

      // Validate template structure for life-critical operation
      this.validateTemplateStructure(result, templateId);

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw new Error(`Failed to load template ${templateId} from GraphQL: ${errorMessage}`);
    }
  }

  /**
   * Validate template structure for life-critical operation (MANDATORY)
   */
  private validateTemplateStructure(template: any, templateId: string): void {
    const requiredFields = ['id', 'name', 'template_config'];
    for (const field of requiredFields) {
      if (!template[field]) {
        throw new Error(`Template ${templateId} missing required field: ${field}`);
      }
    }

    if (!template.template_config.escalation_levels || !Array.isArray(template.template_config.escalation_levels)) {
      throw new Error(`Template ${templateId} missing or invalid escalation_levels`);
    }

    if (template.template_config.escalation_levels.length === 0) {
      throw new Error(`Template ${templateId} must have at least one escalation level`);
    }
  }

  /**
   * Interpolate template parameters into escalation levels
   */
  private interpolateTemplateEscalationLevels(template: any, parameters: any): any[] {
    const escalationLevels = template.template_config.escalation_levels;
    const interpolatedLevels = [];

    for (const level of escalationLevels) {
      const levelStr = JSON.stringify(level);
      let interpolated = levelStr;

      // Replace ${parameter_name} with actual values
      for (const [key, value] of Object.entries(parameters)) {
        const placeholder = `\${${key}}`;
        interpolated = interpolated.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), String(value));
      }

      interpolatedLevels.push(JSON.parse(interpolated));
    }

    return interpolatedLevels;
  }

  /**
   * Build template parameters from routing criteria
   */
  private buildTemplateParameters(criteria: ExpertRoutingCriteria): Record<string, any> {
    return {
      ai_timeout_minutes: 15,
      ai_max_project_value: 5000,
      ai_hourly_rate: 40,
      ai_success_rate: 87,
      ai_complexity_threshold: criteria.projectComplexity <= 4 ? 'medium' : 'high',
      inhouse_timeout_minutes: 240,
      inhouse_max_project_value: 25000,
      inhouse_hourly_rate: 135,
      inhouse_success_rate: 94,
      inhouse_specializations: criteria.requiredSpecializations,
      specialist_timeout_minutes: 480,
      specialist_max_project_value: 100000,
      specialist_hourly_rate: 275,
      specialist_success_rate: 98,
      specialist_specializations: criteria.requiredSpecializations,
      required_specializations: criteria.requiredSpecializations,
      compliance_frameworks: criteria.complianceFrameworks,
      capacity_threshold: 85,
      escalate_on_human_request: true,
      critical_complexity_threshold: 'critical',
      hipaa_required: criteria.complianceFrameworks.includes('HIPAA'),
      sox_required: criteria.complianceFrameworks.includes('SOX'),
      gdpr_required: criteria.complianceFrameworks.includes('GDPR')
    };
  }

  /**
   * Map template tier string to ExpertTier enum
   */
  private mapTierToExpertTier(tier: string): ExpertTier {
    switch (tier) {
      case 'tier3': return 'tier3';
      case 'tier1': return 'tier1';
      case 'tier2': return 'tier2';
      default: return 'tier1';
    }
  }

  /**
   * Main routing decision logic - implements the agreed handoff criteria (FALLBACK)
   * FERP Pattern: Business logic with repository data access
   */
  async shouldHandoffToSpecialist(criteria: ExpertRoutingCriteria): Promise<ExpertTier> {
    // Use template-based routing first
    try {
      return await this.routeUsingTemplate(criteria, 'default');
    } catch (error) {
      console.warn('Template routing failed, using fallback logic:', error);
      return this.shouldHandoffToSpecialistFallback(criteria);
    }
  }

  /**
   * Fallback routing logic (original hardcoded implementation)
   * FERP Pattern: Business logic with repository data access
   */
  private async shouldHandoffToSpecialistFallback(criteria: ExpertRoutingCriteria): Promise<ExpertTier> {
    // Get current team capacity metrics via repository
    const capacityMetrics = await this.actorRepository.getTeamCapacityMetrics('default'); // TODO: Pass actual tenant

    // STRATEGIC VALUE-BASED ROUTING (>$25K always to specialists)
    if (criteria.projectValue > 25000) {
      return 'tier2'; // Strategic handoff to certified specialists
    }

    // ENTERPRISE CUSTOMER PRIORITY
    if (criteria.isEnterpriseAccount && criteria.projectValue > 10000) {
      return 'tier2'; // Enterprise customers get specialists for mid-value projects
    }

    // COMPLIANCE-CRITICAL ROUTING
    if (criteria.complianceFrameworks.length > 1 || criteria.riskProfile === 'critical') {
      return criteria.projectValue > 5000 ? 'tier2' : 'tier1';
    }

    // COMPLEXITY-BASED ROUTING
    if (criteria.complexity >= 8 && criteria.requiresCustomDevelopment) {
      return 'tier2'; // High complexity requires specialists
    }

    // CAPACITY-BASED ROUTING (using repository data)
    if (capacityMetrics.shouldHandoffToSpecialists) {
      return criteria.projectValue > 5000 ? 'tier2' : 'tier3';
    }

    // DEFAULT TO TIER 1 (Internal experts) for standard work
    if (criteria.projectValue > 1000 && criteria.complexity >= 3) return 'tier1';

    // SIMPLE WORK - AI-Enhanced Self-Service
    return 'tier3';
  }

  /**
   * Get expert assignment recommendation using SAAS workflow templates
   * FERP Pattern: Service orchestration with template-driven data access
   */
  async getExpertAssignment(
    criteria: ExpertRoutingCriteria,
    templateType: string = 'timetrex',
    tenantId: string = 'default'
  ): Promise<ExpertAssignment> {
    // Determine recommended tier using SAAS workflow template
    const recommendedTier = await this.routeUsingTemplate(criteria, tenantId);

    // Get experts for the recommended tier via repository
    const availableExperts = await this.actorRepository.findExpertsByTier(tenantId, recommendedTier);

    // Filter by specializations if template-specific
    const specializations = this.getTemplateSpecializations(templateType);
    const qualifiedExperts = await this.actorRepository.findExpertsBySpecializations(
      tenantId,
      specializations
    );

    // Select best expert from qualified candidates
    const assignedExpert = this.selectBestExpert(qualifiedExperts, criteria);

    // Calculate cost and duration
    const expertMetadata = assignedExpert?.metadata as ExpertMetadata;
    const hourlyRate = expertMetadata?.hourlyRate || this.getTierHourlyRate(recommendedTier);
    const estimatedCost = criteria.estimatedHours * hourlyRate;

    // Generate rationale
    const rationale = this.generateRationale(criteria, recommendedTier, assignedExpert);

    // Generate alternative options
    const alternativeOptions = await this.generateAlternativeOptions(
      criteria,
      recommendedTier,
      templateType,
      tenantId
    );

    // Create assignment with confidence scoring
    const confidence = this.calculateConfidence(criteria, assignedExpert, recommendedTier);

    return {
      recommendedTier,
      assignedExpert,
      estimatedCost,
      estimatedDuration: criteria.estimatedHours,
      confidence,
      rationale,
      alternativeOptions
    };
  }

  /**
   * Assign expert to integration project using repository pattern
   * FERP Pattern: Creates workflow instances for state tracking
   */
  async assignExpertToIntegration(
    criteria: ExpertRoutingCriteria,
    expertId: string,
    integrationId: string,
    tenantId: string
  ): Promise<{ assignment: ActorDescriptor; workflowInstance: any }> {
    // Use repository to assign expert
    const assignment = await this.actorRepository.assignExpertToIntegration(
      tenantId,
      expertId,
      integrationId
    );

    // Create a mock workflow instance for the assignment
    const workflowInstance = {
      id: `workflow-${assignment.metadata?.id}-${integrationId}`,
      state: 'assigned',
      expertId,
      integrationId,
      assignedAt: new Date().toISOString(),
      estimatedHours: criteria.estimatedHours,
      hourlyRate: criteria.projectValue / criteria.estimatedHours
    };

    return { assignment, workflowInstance };
  }

  /**
   * Get template-specific specializations
   */
  private getTemplateSpecializations(templateType: string): string[] {
    const specializationMap: Record<string, string[]> = {
      'timetrex': ['payroll_systems', 'timetrex', 'hr_integration'],
      'adp': ['payroll_systems', 'adp', 'hr_integration'],
      'ghost': ['cms', 'content_management', 'web_development'],
      'salesforce': ['crm', 'salesforce', 'sales_automation'],
      'default': ['business_integration', 'api_development']
    };

    return specializationMap[templateType] || specializationMap['default'] || [];
  }

  /**
   * Select best expert from qualified candidates
   */
  private selectBestExpert(experts: ActorDescriptor[], criteria: ExpertRoutingCriteria): ActorDescriptor | undefined {
    if (experts.length === 0) return undefined;

    // Score experts based on multiple factors
    const scoredExperts = experts.map(expert => {
      const metadata = expert.metadata as ExpertMetadata;
      let score = 0;

      // Success rate weight (40%)
      score += (metadata.successRate || 0) * 0.4;

      // Availability weight (30%)
      const availabilityScore = metadata.availabilityStatus === 'available' ? 100 :
                               metadata.currentCapacity < 70 ? 80 : 40;
      score += availabilityScore * 0.3;

      // Compliance match weight (20%)
      const complianceScore = criteria.complianceFrameworks.every(framework => {
        switch (framework.toLowerCase()) {
          case 'hipaa': return metadata.compliance?.hipaaValidated;
          case 'sox': return metadata.compliance?.soxValidated;
          case 'gdpr': return metadata.compliance?.gdprValidated;
          default: return true;
        }
      }) ? 100 : 50;
      score += complianceScore * 0.2;

      // Response time weight (10%)
      const responseScore = Math.max(0, 100 - (metadata.avgResponseTime || 0) * 10);
      score += responseScore * 0.1;

      return { expert, score };
    });

    // Sort by score and return best expert
    scoredExperts.sort((a, b) => b.score - a.score);
    return scoredExperts[0]?.expert;
  }

  /**
   * Calculate confidence score for assignment
   */
  private calculateConfidence(
    criteria: ExpertRoutingCriteria,
    expert?: ActorDescriptor,
    tier?: ExpertTier
  ): number {
    let confidence = 70; // Base confidence

    if (expert) {
      const metadata = expert.metadata as ExpertMetadata;

      // Expert quality factors
      confidence += (metadata.successRate || 0) * 0.2;

      // Availability factors
      if (metadata.availabilityStatus === 'available') confidence += 10;
      if (metadata.currentCapacity < 50) confidence += 10;

      // Compliance match
      const complianceMatch = criteria.complianceFrameworks.every(framework => {
        switch (framework.toLowerCase()) {
          case 'hipaa': return metadata.compliance?.hipaaValidated;
          case 'sox': return metadata.compliance?.soxValidated;
          case 'gdpr': return metadata.compliance?.gdprValidated;
          default: return true;
        }
      });
      if (complianceMatch) confidence += 15;
    }

    // Tier appropriateness
    if (tier === 'tier2' && criteria.projectValue > 25000) confidence += 10;
    if (tier === 'tier1' && criteria.complexity <= 6) confidence += 10;
    if (tier === 'tier3' && criteria.complexity <= 3) confidence += 10;

    return Math.min(100, Math.max(0, confidence));
  }

  /**
   * Get default hourly rate for tier
   */
  private getTierHourlyRate(tier: ExpertTier): number {
    const rates: Record<ExpertTier, number> = {
      'tier1': 135, // Average of $120-150
      'tier2': 225, // Average of $200+
      'tier3': 37,  // Average of $25-50
      'specialist': 300 // Premium specialist rate
    };
    return rates[tier];
  }

  /**
   * Generate rationale for expert assignment
   */
  private generateRationale(
    criteria: ExpertRoutingCriteria,
    tier: ExpertTier,
    expert?: ActorDescriptor
  ): string {
    const reasons: string[] = [];

    // Project value considerations
    if (criteria.projectValue > 25000) {
      reasons.push(`High-value project ($${criteria.projectValue.toLocaleString()}) requires specialist expertise`);
    }

    // Complexity considerations
    if (criteria.complexity >= 8) {
      reasons.push(`High complexity (${criteria.complexity}/10) requires experienced specialist`);
    }

    // Customer tier considerations
    if (criteria.isEnterpriseAccount) {
      reasons.push('Enterprise customer receives priority specialist assignment');
    }

    // Compliance considerations
    if (criteria.complianceFrameworks.length > 0) {
      reasons.push(`Compliance requirements (${criteria.complianceFrameworks.join(', ')}) require certified expert`);
    }

    // Capacity considerations
    if (criteria.internalCapacity > 85) {
      reasons.push('Internal team at capacity, routing to external specialist');
    }

    // Expert-specific reasons
    if (expert) {
      const metadata = expert.metadata as ExpertMetadata;
      if (metadata.successRate > 90) {
        reasons.push(`Selected expert has excellent success rate (${metadata.successRate}%)`);
      }
      if (metadata.avgResponseTime <= 2) {
        reasons.push('Expert provides rapid response for urgent project needs');
      }
    }

    // Default tier rationale
    if (reasons.length === 0) {
      const tierDescriptions: Record<ExpertTier, string> = {
        'tier1': 'Standard project scope suitable for internal FERP experts',
        'tier2': 'Complex project requiring certified specialist expertise',
        'tier3': 'Simple integration suitable for AI-enhanced self-service',
        'specialist': 'High-complexity project requiring specialized expert consultation'
      };
      reasons.push(tierDescriptions[tier]);
    }

    return reasons.join('. ') + '.';
  }

  /**
   * Generate alternative expert options
   */
  private async generateAlternativeOptions(
    criteria: ExpertRoutingCriteria,
    recommendedTier: ExpertTier,
    templateType: string,
    tenantId: string
  ): Promise<ExpertOption[]> {
    const alternatives: ExpertOption[] = [];

    // Generate options for each tier
    const tiers: ExpertTier[] = ['tier1', 'tier2', 'tier3'];

    for (const tier of tiers) {
      if (tier === recommendedTier) continue; // Skip recommended tier

      const experts = await this.actorRepository.findExpertsByTier(tenantId, tier);
      const hourlyRate = this.getTierHourlyRate(tier);
      const cost = criteria.estimatedHours * hourlyRate;

      let description = '';
      let availability = 1; // Default 1 day

      switch (tier) {
        case 'tier1':
          description = 'FERP internal experts - cost-effective for standard integrations';
          availability = 2;
          break;
        case 'tier2':
          description = 'Certified specialists - premium expertise for complex projects';
          availability = 5;
          break;
        case 'tier3':
          description = 'AI-enhanced self-service - rapid deployment for simple integrations';
          availability = 1;
          break;
      }

      alternatives.push({
        tier,
        expert: experts[0], // Use first available expert as example
        cost,
        duration: criteria.estimatedHours,
        availability,
        description
      });
    }

    return alternatives;
  }

  /**
   * Validate compliance requirements for expert
   */
  async validateComplianceRequirements(
    expert: ActorDescriptor,
    complianceFrameworks: string[]
  ): Promise<{ valid: boolean; missingCertifications: string[] }> {
    const metadata = expert.metadata as ExpertMetadata;
    const missingCertifications: string[] = [];

    complianceFrameworks.forEach(framework => {
      const requiredValidation = `${framework.toLowerCase()}Validated`;
      let isValid = false;

      switch (framework.toLowerCase()) {
        case 'hipaa':
          isValid = metadata.compliance?.hipaaValidated || false;
          break;
        case 'sox':
          isValid = metadata.compliance?.soxValidated || false;
          break;
        case 'gdpr':
          isValid = metadata.compliance?.gdprValidated || false;
          break;
      }

      if (!isValid) {
        missingCertifications.push(`${framework}_Expert`);
      }
    });

    return {
      valid: missingCertifications.length === 0,
      missingCertifications
    };
  }
}

// Export singleton instance for use across application
export const expertRoutingService = new ExpertRoutingService();

// Re-export GraphQL queries from @ferp/graphql-client for test file imports
// This maintains backwards compatibility with test files that import from ExpertRoutingService
export {
  GET_TEAM_CAPACITY_METRICS,
  GET_EXPERTS_BY_TIER,
  GET_ACTIVE_ASSIGNMENTS,
  GET_ROUTING_DECISIONS,
  ASSIGN_EXPERT_TO_PROJECT,
  UPDATE_EXPERT_CAPACITY,
  GET_EXPERT_ASSIGNMENT_RECOMMENDATION,
} from '@ferp/graphql-client';